import * as React from "react"
import { cn } from "@/lib/utils"
import { ChevronIcon } from "../icons/Chevron"
import { EthereumLogo, BitcoinLogo, SolanaLogo } from "../icons/crypto"
import { WalletAddressField, WalletAddressFieldProps } from "./wallet-address-field"
import { WalletAddressInput, WalletAddressInputProps } from "./wallet-address-input"

export type WalletType = "ETH" | "BTC" | "SOL"

export interface WalletExpandableProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Type of wallet/cryptocurrency
   */
  walletType: WalletType
  /**
   * Whether this item is expanded
   */
  expanded?: boolean
  /**
   * Callback when expand/collapse is toggled
   */
  onToggle?: () => void
  /**
   * Variant of the content when expanded
   * 'field' shows WalletAddressField (read-only with copy)
   * 'input' shows WalletAddressInput (editable)
   * @default 'field'
   */
  variant?: 'field' | 'input'
  /**
   * Props to pass to WalletAddressField when variant='field'
   */
  fieldProps?: Omit<WalletAddressFieldProps, 'label'>
  /**
   * Props to pass to WalletAddressInput when variant='input'
   */
  inputProps?: Omit<WalletAddressInputProps, 'variant'>
}

/**
 * Expandable wallet component
 *
 * Displays a wallet type with icon and name that can be expanded to show
 * either a wallet address field or input. Based on Figma design node-id 260-1717.
 *
 * @example
 * ```tsx
 * // Field variant (read-only)
 * <WalletExpandable
 *   walletType="ETH"
 *   variant="field"
 *   expanded={false}
 *   onToggle={() => console.log('toggled')}
 *   fieldProps={{
 *     displayValue: "0xAbC123...789DeF",
 *     fullValue: "0xAbC123456789DeF"
 *   }}
 * />
 *
 * // Input variant (editable)
 * <WalletExpandable
 *   walletType="SOL"
 *   variant="input"
 *   expanded={true}
 *   inputProps={{
 *     value: solAddress,
 *     onChange: (e) => setSolAddress(e.target.value),
 *     placeholder: "Enter Solana address"
 *   }}
 * />
 * ```
 */
export const WalletExpandable = React.forwardRef<HTMLDivElement, WalletExpandableProps>(
  ({
    className,
    walletType,
    expanded = false,
    onToggle,
    variant = 'field',
    fieldProps,
    inputProps,
    ...props
  }, ref) => {
    // Configuration for each wallet type
    const walletConfig = {
      ETH: {
        name: "Info ETH",
        icon: EthereumLogo,
        label: "Wallet address :",
        placeholder: "Enter Ethereum address"
      },
      BTC: {
        name: "Info BTC",
        icon: BitcoinLogo,
        label: "Wallet address :",
        placeholder: "Enter Bitcoin address"
      },
      SOL: {
        name: "Info SOL",
        icon: SolanaLogo,
        label: "Wallet address :",
        placeholder: "Enter Solana address"
      }
    }

    const config = walletConfig[walletType]
    const WalletIcon = config.icon

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col",
          "bg-white rounded-[0.3125rem]",
          "transition-all duration-200",
          className
        )}
        {...props}
      >
        {/* Header - Always visible */}
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            "flex items-center justify-between",
            "px-3 py-2",
            "w-full",
            "bg-transparent border-none",
            "cursor-pointer transition-colors",
            "hover:bg-gray-50",
            "focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-1 rounded-[0.3125rem]"
          )}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${config.name}`}
        >
          {/* Left side: Icon + Name */}
          <div className="flex items-center gap-2">
            <WalletIcon size={20} />
            <span
              className="font-montserrat text-[0.9375rem] font-bold leading-[1.25rem] text-gray-500"
              style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
            >
              {config.name}
            </span>
          </div>

          {/* Right side: Chevron icon */}
          <ChevronIcon
            size={12}
            className={cn(
              "text-gray-500 transition-transform duration-200",
              expanded && "rotate-180"
            )}
          />
        </button>

        {/* Expandable content */}
        <div
          className={cn(
            "grid transition-all duration-200 ease-in-out",
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          )}
        >
          <div className="overflow-hidden">
            <div className="px-3 pb-3">
              {variant === 'field' ? (
                <WalletAddressField
                  label={config.label}
                  displayValue={fieldProps?.displayValue || ""}
                  fullValue={fieldProps?.fullValue}
                  onCopy={fieldProps?.onCopy}
                  {...fieldProps}
                />
              ) : (
                <WalletAddressInput
                  label={inputProps?.label || config.label}
                  variant="wallet-address"
                  placeholder={inputProps?.placeholder || config.placeholder}
                  showClearButton={inputProps?.showClearButton ?? true}
                  {...inputProps}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

WalletExpandable.displayName = "WalletExpandable"